import React, { useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { KNOWN_TENANTS } from '../config/tenants';
import './TenantSelector.css';

interface TenantSelectorProps {
    minimalUI?: boolean;
}

/**
 * Componente per selezionare il tenant attivo
 */
const TenantSelector: React.FC<TenantSelectorProps> = ({ minimalUI = false }) => {
    // Usando any per evitare rotture se il tipo del contesto non è ancora aggiornato nel build corrente
    const ctx: any = useTenant();
    const { tenantId, loading, error } = ctx;
    const changeTenant: (id: string) => void = ctx.changeTenant || ((newTenantId: string) => {
        if (!newTenantId) return;
        localStorage.setItem('tenantId', newTenantId);
        try {
            const sp = new URLSearchParams(window.location.search);
            sp.set('tenantId', newTenantId);
            const base = window.location.pathname.split('?')[0];
            const newUrl = `${base}?${sp.toString()}`;
            window.history.pushState({}, '', newUrl);
        } catch (_) { /* ignore */ }
        // Forza il reload dei dati se disponibile
        if (ctx.reloadData) ctx.reloadData();
    });
    const [inputTenantId, setInputTenantId] = useState(tenantId || '');
    const [isExpanded, setIsExpanded] = useState(false);

    const [showKnownTenants, setShowKnownTenants] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (inputTenantId && inputTenantId.trim()) {
            changeTenant(inputTenantId.trim());
            setIsExpanded(false);
            setShowKnownTenants(false);
        }
    };

    const handleSelectKnownTenant = (tenantId: string) => {
        changeTenant(tenantId);
        setInputTenantId(tenantId);
        setIsExpanded(false);
        setShowKnownTenants(false);
    };

    if (minimalUI) {
        return (
            <div className="tenant-selector tenant-selector--minimal">
                <button
                    className="tenant-selector__toggle"
                    onClick={() => setIsExpanded(!isExpanded)}
                    title="Cambia tenant"
                >
                    {tenantId ? `Tenant: ${tenantId}` : 'Seleziona tenant'}
                </button>

                {isExpanded && (
                    <div className="tenant-selector__dropdown">
                        <form className="tenant-selector__form" onSubmit={handleSubmit}>
                            <input
                                type="text"
                                value={inputTenantId}
                                onChange={(e) => setInputTenantId(e.target.value)}
                                placeholder="ID tenant"
                                className="tenant-selector__input"
                            />
                            <button type="submit" className="tenant-selector__submit">OK</button>
                        </form>

                        <div className="tenant-selector__known-tenants">
                            <button
                                className="tenant-selector__toggle-list"
                                onClick={() => setShowKnownTenants(!showKnownTenants)}
                            >
                                {showKnownTenants ? 'Nascondi lista' : 'Mostra tenant disponibili'}
                            </button>

                            {showKnownTenants && (
                                <ul className="tenant-selector__list">
                                    {KNOWN_TENANTS.map(tenant => (
                                        <li key={tenant.id} className="tenant-selector__list-item">
                                            <button
                                                className={`tenant-selector__tenant-button ${tenant.id === tenantId ? 'active' : ''}`}
                                                onClick={() => handleSelectKnownTenant(tenant.id)}
                                            >
                                                {tenant.name} (ID: {tenant.id})
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="tenant-selector">
            <h4 className="tenant-selector__title">Seleziona Tenant</h4>
            <form className="tenant-selector__form" onSubmit={handleSubmit}>
                <input
                    type="text"
                    value={inputTenantId}
                    onChange={(e) => setInputTenantId(e.target.value)}
                    placeholder="Inserisci ID tenant"
                    className="tenant-selector__input"
                />
                <button
                    type="submit"
                    className="tenant-selector__submit"
                    disabled={loading}
                >
                    {loading ? 'Caricamento...' : 'Cambia'}
                </button>
            </form>

            {error && <div className="tenant-selector__error">{error}</div>}
            {tenantId && <div className="tenant-selector__current">Tenant attuale: {tenantId}</div>}

            <div className="tenant-selector__known-tenants">
                <h5 className="tenant-selector__subtitle">Tenant disponibili:</h5>
                <div className="tenant-selector__grid">
                    {KNOWN_TENANTS.map(tenant => (
                        <div
                            key={tenant.id}
                            className={`tenant-selector__tenant-card ${tenant.id === tenantId ? 'active' : ''} ${!tenant.isActive ? 'inactive' : ''}`}
                            onClick={() => tenant.isActive && handleSelectKnownTenant(tenant.id)}
                            title={tenant.isActive ? tenant.description : 'Tenant non attivo'}
                        >
                            <div className="tenant-selector__tenant-name">{tenant.name}</div>
                            <div className="tenant-selector__tenant-id">ID: {tenant.id}</div>
                            <div className="tenant-selector__tenant-type">{tenant.type}</div>
                            {!tenant.isActive && <div className="tenant-selector__tenant-inactive">Non attivo</div>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TenantSelector;
